// First define the Priority type
export type Priority = 'low' | 'medium' | 'high';

// Then define ColumnType
// Allow arbitrary column keys to support configurable workflow statuses
export type ColumnType = string;

// Define story type
export type StoryType = 'story' | 'bug' | 'task';

// Story Comment interface
export interface StoryComment {
  id: number;
  storyId: number;
  text: string;
  author: {
    id: number;
    username: string;
  };
  createdAt: string;
}

// Work Log interface
export interface WorkLog {
  id: number;
  taskId: number;
  userId: number;
  description: string;
  hours?: number | null;
  createdAt: string;
  actorFirstName?: string; // Added to include actor's first name
  actorLastName?: string;  // Added to include actor's last name
}

// Attachment interface
export interface Attachment {
  id: number;
  originalFilename: string;
  url: string;
  thumbnailUrl?: string;
}

// Extended Task interface that can handle both Kanban and Backlog items
export interface Task {
  id: string | number; // Support both string and number IDs
  title: string;
  description: string;
  priority: Priority;
  taskColumn?: ColumnType; // Optional for backlog items
  assignee?: string;
  assigneeInitials?: string;
  // Additional fields for backlog functionality
  storyPoints?: number;
  type?: StoryType;
  assigneeUserId?: string | null; // User ID for task assignment
  teamId?: string; // Team ID for task assignment
  status?: 'backlog' | string; // For sprint assignment (sprint IDs)
  // Creation and modification tracking
  createdByUserId?: string;
  createdByUserName?: string;
  createdAt?: string;
  updatedAt?: string;
  // Additional fields from API
  cancelled?: any;
  dueDate?: string | null;
  // File attachments
  attachmentUrls?: string[]; // Legacy format
  attachments?: Attachment[]; // New format
}

// Add this if you need it for drag and drop
export interface DropResult {
  taskColumn: ColumnType;
}

export interface ApiResponse {
  success: boolean;
  message?: string;
}

// Plan interfaces for pricing and plan management
export interface PlanBillingOption {
  id: number;
  billingCycle: string;
  priceCents: number;
  formattedPrice: string;
  isDefault: boolean;
  savingsPercentage?: number | null;
}

export interface PlanFeature {
  featureId: number;
  featureName: string;
  featureCode?: string | null;
  value: string;
}

export interface PlanLimits {
  maxUsers: number;
  maxTeams: number;
  storageBytes?: number | null;
  formattedStorage: string;
  supportType: string;
}

export interface Plan {
  id: number;
  key: string;
  name: string;
  description: string;
  isMostPopular: boolean;
  billingOptions: PlanBillingOption[];
  features: PlanFeature[];
  limits: PlanLimits;
  organizationCount?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface PublicPlansResponse {
  plans: Plan[];
  meta: {
    totalPlans: number;
    timestamp: string;
  };
}

// Progressive Pricing Enums
export enum ProgressivePricingTire {
  FREE = 'FREE',
  TOPUP_5 = 'TOPUP_5',
  TOPUP_10 = 'TOPUP_10',
  TOPUP_20 = 'TOPUP_20',
  TOPUP_30 = 'TOPUP_30',
  TOPUP_40 = 'TOPUP_40',
  TOPUP_50 = 'TOPUP_50',
  TOPUP_100 = 'TOPUP_100',
  TOPUP_250 = 'TOPUP_250',
  TOPUP_PROMOTIONAL = 'TOPUP_PROMOTIONAL',
  TOPUP_CREDIT = 'TOPUP_CREDIT',
  TOPUP_CUSTOM = 'TOPUP_CUSTOM',
}

export enum ProgressivePricingType {
  USERS = 'USERS',
  PROJECTS = 'PROJECTS',
  STORAGE = 'STORAGE',
  API_REQUESTS = 'API_REQUESTS',
  CUSTOM = 'CUSTOM',
  STORIES = 'STORIES'
}

// Progressive Pricing Plan interface
export interface ProgressivePricingPlan {
  id: number;
  planKey: string;
  tire: ProgressivePricingTire;
  type: ProgressivePricingType;
  planLimit: number;
  isActive: boolean;
  priceCents: number;
  createdAt: string;
  updatedAt: string;
  effectiveDate: string;
}

export interface ProgressivePlansResponse {
  data: {
    plans: ProgressivePricingPlan[];
  };
  success: boolean;
}

// Progressive Plan Quota interface
export interface ProgressivePlanQuota {
  id: number;
  organizationId: number;
  progressivePricingType: ProgressivePricingType;
  quota: number;
  balance: number;
}

// Organization with quotas interface
export interface OrganizationWithQuotas {
  id: number;
  name: string;
  industry?: string;
  size?: string;
  website?: string;
  users: any[];
  plan: any;
  progressiveUsersPlan: any;
  progressiveStoriesPlan: any;
  progressivePlanQuotas: ProgressivePlanQuota[];
}